/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * MuiButton component overrides for the Renaissance design system.
 * All contained variants derive styles from palette colors via glassButton().
 */

import { alpha } from '@mui/material/styles'
import type { TPaletteDefinition } from '../palette'
import { glassButton, glassButtonNeutral } from '../glass'
import { focusRing } from '../focusRing'
import {
  radii,
  spacing,
  sizing,
  target,
  hitAreaInset,
  transitionFor,
} from '../tokens'

export function buttonOverrides(
  palette: TPaletteDefinition,
  mode: 'dark' | 'light',
  reducedMotion = false
) {
  const buttonTransition = transitionFor(
    'feedback',
    ['background-color', 'box-shadow', 'border-color'],
    reducedMotion
  )
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: radii.full,
          textAlign: 'center' as const,
          fontWeight: 600,
          lineHeight: 1,
          fontSize: 13,
          height: sizing.buttonHeight,
          minHeight: sizing.buttonHeight,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: spacing['3xl'],
          paddingRight: spacing['3xl'],
          transition: buttonTransition,
          position: 'relative' as const,
          // Hit area vs. drawn size (plan task 1.4): a mouse is precise
          // enough that the button can stay drawn at `sizing.buttonHeight`;
          // an invisible `::after` still grows its actual clickable region
          // to the `target.min` pointer target. Under a coarse (touch)
          // pointer the drawn control itself grows to `target.coarseMin`
          // instead, so the enlarged region becomes visible too.
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: `${hitAreaInset(sizing.buttonHeight)}px`,
          },
          '@media (pointer: coarse)': {
            height: target.coarseMin,
            minHeight: target.coarseMin,
            '&::after': { inset: 0 },
          },
          // MuiButton is `styled(ButtonBase)`: the root itself is the
          // focused DOM node, but MUI's own docs still call for the
          // `Mui-focusVisible` class (its `useIsFocusVisible` polyfill, not
          // native `:focus-visible`) for custom focus styling — see
          // `ButtonBase`'s own propTypes comment.
          '&.Mui-focusVisible': focusRing(palette, mode),
        },
      },
      variants: [
        {
          props: { variant: 'contained' as const, color: 'primary' as const },
          style: glassButton(
            palette.primary.main,
            mode,
            palette.primary.forcedContrastText
          ),
        },
        {
          props: {
            variant: 'contained' as const,
            color: 'secondary' as const,
          },
          style: {
            ...glassButtonNeutral(mode, palette.text.main),
            backgroundColor:
              mode === 'dark' ? 'rgba(30,30,30,0.45)' : 'rgba(255,255,255,0.4)',
            backdropFilter: 'blur(6px) saturate(180%)',
            WebkitBackdropFilter: 'blur(6px) saturate(180%)',
            transition: buttonTransition,
            ...(mode === 'light' && {
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.06)',
                backgroundImage: 'none',
                border: '1px solid rgba(0,0,0,0.12)',
                boxShadow:
                  '0 2px 10px rgba(0,0,0,0.08), inset 0 1px 0 rgba(160,160,170,0.3)',
              },
              '&:active': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                backgroundImage: 'none',
                border: '1px solid rgba(0,0,0,0.14)',
                boxShadow:
                  '0 1px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(160,160,170,0.35)',
              },
            }),
          },
        },
        {
          props: { variant: 'contained' as const, color: 'success' as const },
          style: glassButton(palette.success.main, mode),
        },
        {
          props: { variant: 'contained' as const, color: 'error' as const },
          style: glassButton(palette.error.main, mode),
        },
        {
          props: { variant: 'contained' as const, color: 'warning' as const },
          style: glassButton(
            palette.warning.main,
            mode,
            palette.text.light // Dark text on yellow for WCAG compliance
          ),
        },
        {
          props: { variant: 'contained' as const, color: 'info' as const },
          style: glassButton(palette.info.main, mode),
        },
        {
          props: { variant: 'text' as const, color: 'primary' as const },
          style: {
            color: palette.primary.main,
          },
        },
        {
          props: { variant: 'text' as const, color: 'secondary' as const },
          style: {
            color: palette.text.main,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: alpha(palette.text.main, 0.04),
            },
            '&:active': {
              backgroundColor: alpha(palette.text.main, 0.1),
            },
          },
        },
        // Tab variant: text button used inside UITabs
        {
          props: { variant: 'text' as const },
          style: {
            '&.UIButton-tab': {
              color: palette.text.main,
              fontWeight: 600,
              fontSize: 12,
              height: sizing.buttonHeight - 8,
              minHeight: sizing.buttonHeight - 8,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: palette.gray5.main,
              },
              '&:active': {
                backgroundColor: palette.gray4.main,
                transform: 'scale(0.96)',
              },
              // The root's own `::after` still expands the hit area (sized
              // for the base button height, not recalculated for this
              // shorter variant — left alone deliberately: tabs sit edge to
              // edge inside a segmented control with no gap, so growing it
              // further here would overlap the next tab's hit zone). The
              // drawn height still rises under a coarse pointer like every
              // other control; this rule's own specificity, later in
              // source, is what wins over the plain `height` above once the
              // media query matches.
              '@media (pointer: coarse)': {
                height: target.coarseMin,
                minHeight: target.coarseMin,
              },
            },
            '&.UIButton-tab-active': {
              backgroundColor: palette.gray5.main,
              color: palette.blue.main,
              fontWeight: 600,
              '&:active': {
                backgroundColor: palette.gray4.main,
                transform: 'scale(0.96)',
              },
            },
          },
        },
      ],
    },
  }
}
